// Wayne AI Platform — typed API client wrapper.
//
// Configures the @hey-api/openapi-ts-generated fetch client with:
//   - `credentials: 'include'` so the Midway HttpOnly cookie rides every call.
//   - The double-submit CSRF pattern: read the non-HttpOnly `wayne_csrf_token`
//     cookie, mirror it into the `X-CSRF-Token` header on every state-changing
//     request.
//   - 401 handling: full-page redirect to `/api/auth/initiate?redirect=…` so
//     the user lands back where they were after the Midway round-trip.
//   - 429 handling: throws a typed `RateLimitedError` with `retryAfterSeconds`
//     parsed from the `Retry-After` header.
//   - All other non-2xx responses throw a typed `ApiError`. We never surface
//     `error.message` from the server — the message is a fixed, generic string.
//
// CONTRACT (do not violate)
//   - No tokens in JS. Midway auth cookies are HttpOnly. We only read the CSRF
//     cookie, which intentionally is non-HttpOnly.
//   - No `any`. Use `unknown` and narrow.
//   - 401 anywhere triggers `redirectToLogin()`.

import { client as generatedClient } from './generated/sdk.gen';

const API_BASE = '/api';
const CSRF_COOKIE = 'wayne_csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';
const LOGIN_PATH = '/api/auth/initiate';

const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Base error class for typed API failures. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly correlationId: string | undefined;

  constructor(message: string, status: number, correlationId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.correlationId = correlationId;
  }
}

/** Subclass surfaced when the server returns 429 Too Many Requests. */
export class RateLimitedError extends ApiError {
  public readonly retryAfterSeconds: number;

  constructor(retryAfterSeconds: number, correlationId?: string) {
    super('Rate limit exceeded. Please wait a moment.', 429, correlationId);
    this.name = 'RateLimitedError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Read a non-HttpOnly cookie value by name. Returns '' if missing/malformed. */
export function readCookie(name: string, source?: string): string {
  const raw = source ?? (typeof document !== 'undefined' ? document.cookie : '');
  if (!raw) return '';
  const cookies = raw.split(';');
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    const eq = trimmed.indexOf('=');
    if (eq === -1) continue;
    const cookieName = trimmed.substring(0, eq).trim();
    if (cookieName === name) {
      try {
        return decodeURIComponent(trimmed.substring(eq + 1));
      } catch {
        return '';
      }
    }
  }
  return '';
}

/**
 * Send the user to Midway. We pass `redirect` so the backend can deep-link
 * them back to the URL that triggered the 401.
 */
export function redirectToLogin(currentUrl?: string): void {
  if (typeof window === 'undefined') return;
  const here =
    currentUrl ?? `${window.location.pathname}${window.location.search}`;
  const url = `${LOGIN_PATH}?redirect=${encodeURIComponent(here)}`;
  window.location.assign(url);
}

interface RequestOptionsMeta {
  method?: string;
}

/**
 * Build a new Request from `request` with CSRF + JSON content-type applied.
 * Pure helper, exported for unit tests.
 */
export function applyRequestDefaults(
  request: Request,
  options: RequestOptionsMeta,
  cookieSource?: string,
): Request {
  const method = (options.method ?? request.method ?? 'GET').toUpperCase();
  const headers = new Headers(request.headers);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (STATE_CHANGING_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE, cookieSource);
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }
  return new Request(request, {
    headers,
    credentials: 'include',
  });
}

/**
 * Inspect a `Response` and either return it (2xx) or throw a typed error.
 * Exported for unit tests; the runtime uses the response interceptor below.
 */
export async function handleResponseStatus(
  response: Response,
): Promise<Response> {
  if (response.ok) return response;

  if (response.status === 401) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      redirectToLogin();
    }
    throw new ApiError('Authentication required', response.status);
  }

  if (response.status === 403) {
    throw new ApiError('Forbidden', response.status);
  }

  // Try to extract correlation id; tolerate non-JSON bodies.
  let correlationId: string | undefined;
  let retryAfterSeconds: number | undefined;
  try {
    const cloned = response.clone();
    const body = (await cloned.json()) as Partial<{
      correlationId: string;
      retryAfterSeconds: number;
    }>;
    if (typeof body.correlationId === 'string') correlationId = body.correlationId;
    if (typeof body.retryAfterSeconds === 'number')
      retryAfterSeconds = body.retryAfterSeconds;
  } catch {
    // Body wasn't JSON — fine.
  }

  if (response.status === 429) {
    const headerVal = response.headers.get('Retry-After');
    const headerSeconds = headerVal ? Number.parseInt(headerVal, 10) : NaN;
    const seconds = Number.isFinite(headerSeconds)
      ? headerSeconds
      : (retryAfterSeconds ?? 5);
    throw new RateLimitedError(seconds, correlationId);
  }

  throw new ApiError('Request failed', response.status, correlationId);
}

let configured = false;

/**
 * Configure the singleton generated client. Called once at app boot from
 * `queryClient.ts`. Idempotent for tests that exercise it in jsdom.
 */
export function configureApiClient(options?: { baseUrl?: string }): void {
  generatedClient.setConfig({
    baseUrl: options?.baseUrl ?? API_BASE,
    credentials: 'include',
  });

  if (configured) return;
  configured = true;

  generatedClient.interceptors.request.use((request: Request, requestOptions: unknown) => {
    const meta = requestOptions as RequestOptionsMeta;
    return applyRequestDefaults(request, meta);
  });

  generatedClient.interceptors.response.use((response: Response) =>
    handleResponseStatus(response),
  );
}

/** Reset interceptor state. Test helper, not for production code. */
export function __resetApiClientForTests(): void {
  configured = false;
}

/** Re-export the generated singleton. Callers prefer `configureApiClient()`,
 * then call generated functions like `getMe()` directly. */
export { generatedClient as apiClient };
